import ActivitiesDataService from '@/services/ActivitiesDataService';
import SearchDataService from '@/services/SearchDataService';

/* Cidade de origem da plataforma, usada como filtro inicial da pagina. */
const CIDADE_PADRAO = 'São Bento';

/*
 * A API responde `found_rows` e `total` zerados, entao nao da para saber o
 * total de antemao: a contagem exibida e a do que ja foi carregado, e o botao
 * de carregar mais aparece enquanto a pagina vier cheia.
 */
const TAMANHO_DA_PAGINA = 12;

/* Consultas de apoio, que nao vao para a tela e precisam do conjunto inteiro. */
const LIMITE_DE_CONSULTA = 200;

const formatadorDePreco = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/*
 * Duas taxonomias diferentes convivem aqui:
 *
 * - segmento (`/v1/activities`) pertence a plataforma, vale para loja e filtra
 *   no servidor. E o que alimenta o carrossel;
 * - categoria (`name_category`) pertence ao PrestaShop de cada loja, so existe
 *   em produto e nao tem filtro na API. Vira refino no proprio navegador.
 */
function chaveDeCategoria(nome) {
  const base = String(nome || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  /* Cada lojista escreve como quer: "Rede" e "Redes" precisam cair no mesmo grupo. */
  return base.endsWith('s') ? base.slice(0, -1) : base;
}

function juntar(partes, separador) {
  return partes.filter(Boolean).join(separador);
}

/* `logo_banner_link` vem nulo em todas as lojas; `logo_link` e o que existe. */
function imagemDaLoja(loja) {
  const arquivo = loja.logo_banner_link || loja.logo_link;
  return arquivo ? loja.shop_link + arquivo : '';
}

/*
 * A posicao entra no identificador porque a API repete `id_product` em alguns
 * registros, e a listagem precisa de chave unica. Ela carrega a pagina junto
 * para que o indice nao se repita a cada pagina carregada.
 */
function paraCartaoDeLoja(loja, posicao) {
  return {
    id: `loja-${posicao}-${loja.id_shop}`,
    titulo: loja.name,
    categoria: loja.activity,
    chaveDeCategoria: '',
    detalhe: juntar([loja.city, loja.state], ' - '),
    detalheEmDestaque: false,
    complemento: juntar([loja.address1, loja.address2], ', '),
    imagem: imagemDaLoja(loja),
    link: loja.shop_link
  };
}

function paraCartaoDeProduto(produto, posicao) {
  return {
    id: `produto-${posicao}-${produto.id_product}`,
    titulo: produto.name,
    categoria: produto.name_category,
    chaveDeCategoria: chaveDeCategoria(produto.name_category),
    detalhe: produto.price > 0 ? formatadorDePreco.format(produto.price) : '',
    detalheEmDestaque: true,
    complemento: `Vendido por ${produto.shopname}`,
    imagem: produto.shop_link + produto.image_link,
    link: produto.shop_link + produto.product_link
  };
}

function paraSegmento(atividade) {
  return {
    id: atividade.id,
    nome: atividade.name,
    adulto: Boolean(atividade.isAdult),
    imagem: `assets/img/activities/${atividade.id}.png`
  };
}

function objetosDaResposta(resposta) {
  const dados = resposta && resposta.data;
  return (dados && dados.objects) || [];
}

function unirSemRepetir(listas, chave) {
  const vistos = new Set();

  return listas.reduce((resultado, lista) => {
    lista.forEach((item) => {
      if (vistos.has(item[chave])) return;

      vistos.add(item[chave]);
      resultado.push(item);
    });

    return resultado;
  }, []);
}

/*
 * A API so aceita um `activity` por chamada: com varios segmentos marcados,
 * dispara uma busca para cada e junta os resultados.
 */
function buscarPorSegmento(parametros, segmentos) {
  return Promise.all(segmentos.map((activity) => SearchDataService
    .getSearch(Object.assign({}, parametros, { activity }))
    .then(objetosDaResposta)));
}

/*
 * Alem do limite de um `activity` por chamada, a API aceita o parametro na
 * busca de produtos mas ignora o filtro. Para o segmento valer nos dois
 * recursos, descobrimos aqui quais lojas pertencem aos segmentos marcados e
 * mantemos apenas os produtos delas.
 */
async function linksDasLojasDosSegmentos(segmentos, cidade) {
  const listas = await buscarPorSegmento({
    res: 'shops',
    city: cidade || undefined,
    page: 1,
    pSize: LIMITE_DE_CONSULTA
  }, segmentos);

  const links = new Set();
  listas.forEach((lojas) => lojas.forEach((loja) => links.add(loja.shop_link)));

  return links;
}

/*
 * Sem `total` na resposta, a existencia de mais paginas e deduzida do tamanho
 * da que chegou: pagina cheia significa que ainda pode haver o que carregar.
 */
async function buscarPagina({ recurso, termo, segmentosSelecionados, cidade }, pagina) {
  const parametros = {
    res: recurso,
    page: pagina,
    pSize: TAMANHO_DA_PAGINA
  };

  if (termo) parametros.q = termo;
  if (cidade) parametros.city = cidade;

  const posicaoDe = (indice) => `${pagina}-${indice}`;

  if (recurso === 'shops') {
    if (!segmentosSelecionados.length) {
      const lojas = objetosDaResposta(await SearchDataService.getSearch(parametros));

      return {
        itens: lojas.map((loja, indice) => paraCartaoDeLoja(loja, posicaoDe(indice))),
        temMais: lojas.length === TAMANHO_DA_PAGINA
      };
    }

    const listas = await buscarPorSegmento(parametros, segmentosSelecionados);

    return {
      itens: unirSemRepetir(listas, 'id_shop')
        .map((loja, indice) => paraCartaoDeLoja(loja, posicaoDe(indice))),
      /* Basta um segmento com pagina cheia para ainda haver o que carregar. */
      temMais: listas.some((lista) => lista.length === TAMANHO_DA_PAGINA)
    };
  }

  const produtos = objetosDaResposta(await SearchDataService.getSearch(parametros));
  /* Medido antes do recorte por segmento, que so acontece no navegador. */
  const temMais = produtos.length === TAMANHO_DA_PAGINA;

  const selecionados = segmentosSelecionados.length
    ? await linksDasLojasDosSegmentos(segmentosSelecionados, cidade)
    : null;

  const visiveis = selecionados
    ? produtos.filter((produto) => selecionados.has(produto.shop_link))
    : produtos;

  return {
    itens: visiveis.map((produto, indice) => paraCartaoDeProduto(produto, posicaoDe(indice))),
    temMais
  };
}

/*
 * Identifica a busca em andamento: com filtros trocados em sequencia, uma
 * resposta atrasada nao pode sobrescrever o resultado da busca mais recente.
 */
let requisicaoAtual = 0;

export default {
  namespaced: true,

  state: {
    recurso: 'shops',
    termo: '',
    /* Cidade unica da plataforma; nao ha selecao de outras cidades. */
    cidade: CIDADE_PADRAO,
    segmentosSelecionados: [],
    categoria: '',
    segmentos: [],
    itens: [],
    pagina: 1,
    /* Maior pagina ja descoberta: a API nao informa o total. */
    paginasConhecidas: 1,
    carregando: false,
    mensagemDeErro: '',
    /* Vale enquanto a pagina estiver aberta; nao e gravada em lugar nenhum. */
    maioridadeConfirmada: false
  },

  getters: {
    possuiFiltrosAtivos: (state) => Boolean(state.termo.trim())
      || state.segmentosSelecionados.length > 0
      || Boolean(state.categoria),

    /*
     * Montado a partir do resultado carregado, e nao do que sobra depois do
     * refino: senao a lista encolheria para uma unica opcao ao escolher uma.
     *
     * Entra toda categoria presente no resultado, inclusive as de um unico
     * produto: esconde-las tiraria do usuario o unico caminho ate esse item.
     */
    categoriasDeProduto: (state) => {
      const grupos = new Map();

      state.itens.forEach((item) => {
        if (!item.chaveDeCategoria) return;

        const grupo = grupos.get(item.chaveDeCategoria)
          || { chave: item.chaveDeCategoria, rotulos: {}, total: 0 };

        grupo.rotulos[item.categoria] = (grupo.rotulos[item.categoria] || 0) + 1;
        grupo.total += 1;
        grupos.set(item.chaveDeCategoria, grupo);
      });

      return Array.from(grupos.values())
        .map((grupo) => ({
          chave: grupo.chave,
          /* Entre as grafias do grupo, vale a mais usada pelos lojistas. */
          rotulo: Object.keys(grupo.rotulos)
            .sort((a, b) => grupo.rotulos[b] - grupo.rotulos[a])[0],
          total: grupo.total
        }))
        /* As categorias com mais produtos primeiro; o resto em ordem alfabetica. */
        .sort((a, b) => b.total - a.total || a.rotulo.localeCompare(b.rotulo, 'pt-BR'));
    },

    itensVisiveis: (state) => (state.categoria
      ? state.itens.filter((item) => item.chaveDeCategoria === state.categoria)
      : state.itens)
  },

  mutations: {
    definirRecurso(state, valor) {
      state.recurso = valor;
    },

    definirTermo(state, valor) {
      state.termo = valor;
    },

    /*
     * Reordena pela sequencia do carrossel para a listagem nao depender da
     * ordem em que o usuario clicou nos segmentos.
     */
    alternarSegmento(state, id) {
      const selecionados = state.segmentosSelecionados.includes(id)
        ? state.segmentosSelecionados.filter((atual) => atual !== id)
        : state.segmentosSelecionados.concat(id);

      const ordem = state.segmentos.map((segmento) => segmento.id);

      state.segmentosSelecionados = selecionados
        .sort((a, b) => ordem.indexOf(a) - ordem.indexOf(b));
    },

    definirCategoria(state, valor) {
      state.categoria = valor;
    },

    definirSegmentos(state, valor) {
      state.segmentos = valor;
    },

    confirmarMaioridade(state) {
      state.maioridadeConfirmada = true;
    },

    definirItens(state, valor) {
      state.itens = valor;
    },

    definirPagina(state, valor) {
      state.pagina = valor;
    },

    definirPaginasConhecidas(state, valor) {
      state.paginasConhecidas = valor;
    },

    definirCarregando(state, valor) {
      state.carregando = valor;
    },

    definirMensagemDeErro(state, valor) {
      state.mensagemDeErro = valor;
    },

    limparFiltros(state) {
      state.termo = '';
      state.segmentosSelecionados = [];
      state.categoria = '';
    }
  },

  actions: {
    async INICIALIZAR({ commit, dispatch }) {
      try {
        const atividades = await ActivitiesDataService.getAll();

        /* `isEmpty` marca o segmento sem loja cadastrada: fora do carrossel. */
        const segmentos = (atividades.data || [])
          .filter((atividade) => !atividade.isEmpty)
          .map(paraSegmento);

        commit('definirSegmentos', segmentos);
      } catch (erro) {
        commit('definirMensagemDeErro', 'Não foi possível carregar o catálogo.');
      }

      await dispatch('BUSCAR');
    },

    async BUSCAR({ commit, dispatch }) {
      commit('definirPaginasConhecidas', 1);
      await dispatch('IR_PARA_PAGINA', 1);
    },

    async IR_PARA_PAGINA({ commit, dispatch, state }, pagina) {
      const requisicao = ++requisicaoAtual;

      commit('definirCarregando', true);
      commit('definirMensagemDeErro', '');
      /* O refino vale para a pagina exibida; trocar de pagina recomeca sem ele. */
      commit('definirCategoria', '');

      try {
        const { itens, temMais } = await buscarPagina(state, pagina);

        if (requisicao !== requisicaoAtual) return;

        /*
         * A pagina anterior veio cheia, mas nao havia proxima: acontece quando o
         * total e multiplo exato do tamanho da pagina. Fixa o fim e volta.
         */
        if (!itens.length && pagina > 1) {
          commit('definirPaginasConhecidas', pagina - 1);
          await dispatch('IR_PARA_PAGINA', pagina - 1);
          return;
        }

        commit('definirItens', itens);
        commit('definirPagina', pagina);
        commit('definirPaginasConhecidas', Math.max(
          state.paginasConhecidas,
          temMais ? pagina + 1 : pagina
        ));
      } catch (erro) {
        if (requisicao !== requisicaoAtual) return;

        commit('definirItens', []);
        commit('definirMensagemDeErro', 'Não foi possível carregar os resultados. Tente de novo.');
      } finally {
        if (requisicao === requisicaoAtual) commit('definirCarregando', false);
      }
    }
  }
};
